// src/modules/inventory/pages/InventoryListPage.tsx

import React, { useEffect, useState } from "react";
import InventoryTable from "../components/InventoryTable";
import InventorySearchBar from "../components/InventorySearchBar";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

export interface InventoryItem {
  _id: string;
  name: string;
  sku: string;
  category: string;
  stock: number;
  reorderLevel: number;
  supplier?: string;
  price: number;
  imageUrl?: string;
  status?: string; // e.g., "active", "discontinued"
}

const InventoryListPage: React.FC = () => {
  useProtectedRoute();

  const { token } = useAuth();

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError("You are not authenticated. Please login.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    api
      .get("/inventory", {
        params: { search, category },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setItems(res.data))
      .catch((err) => {
        setError(
          err?.response?.status === 401
            ? "Session expired. Please login again."
            : err?.response?.data?.message || "Failed to fetch inventory items."
        );
      })
      .finally(() => setLoading(false));
  }, [search, category, token]);

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Inventory</h1>
      </div>
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}
      <InventorySearchBar
        value={search}
        onChange={setSearch}
        category={category}
        onCategoryChange={setCategory}
      />
      <div className="mt-4">
        <InventoryTable items={items} loading={loading} />
      </div>

      {/* === Quick Actions Section (as Inventory Dashboard) === */}
      <div className="mt-10">
        <h2 className="text-xl font-bold mb-4 text-gray-700">Quick Actions</h2>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
          <button
            onClick={() => navigate("/inventory/add")}
            className="bg-green-600 hover:bg-green-700 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            + Add Inventory Item
          </button>
          <button
            onClick={() => navigate("/inventory/adjust")}
            className="bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            Adjust Inventory
          </button>
          <button
            onClick={() => navigate("/suppliers")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            Suppliers
          </button>
          <button
            onClick={() => navigate("/suppliers/add")}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            + Add Supplier
          </button>
          <button
            onClick={() => navigate("/purchase-orders")}
            className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            Purchase Orders
          </button>
          <button
            onClick={() => navigate("/purchase-orders/create")}
            className="bg-pink-600 hover:bg-pink-700 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            + Create Purchase Order
          </button>
          <button
            onClick={() => navigate("/inventory/movement-log")}
            className="bg-gray-700 hover:bg-gray-800 text-white rounded-xl py-4 px-4 shadow font-semibold text-lg transition"
          >
            Stock Movement Log
          </button>
        </div>
      </div>
    </div>
  );
};

export default InventoryListPage;
